import { Auth } from "aws-amplify";
import React, { useContext, useState } from "react";
import { Badge, Button, Card, Col, Container, ListGroup, Row } from "react-bootstrap";
import { FaCalendarAlt, FaCog, FaCopy, FaEnvelope, FaPaw, FaReceipt, FaSignOutAlt, FaTrash, FaUser } from "react-icons/fa";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";

import { EditableModal } from "../components/editableModal";
import { INPUT_TYPE } from "../constants";
import AppContext from "../context/app-context";
import userProfileService from "../services/userProfileService";
import ExpenseTrackingMetadataDB from "../storage/ExpenseTrackingMetadataDB";
import { delay } from "../utils/helperFunctions";
import { CREATE_CONFIG_CONFIGS } from "../utils/constant";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";

const ProfilePage = () => {
  const { t } = useTranslation();
  const { userProfile = {}, setLoading, setAppRefresh, trackingItems, loadItemsFromOnline } = useContext(AppContext);

  const navigate = useNavigate();
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const handleDeleteAllExpenses = () => {
    setShowDeleteAllModal(true);
  };
  if (!userProfile) return;

  const { fieldConfigs = [], userId } = userProfile;
  const receiptEmail = `receipt+${userId}@${window.location.host}`;

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(receiptEmail);
      toast.success(t("profile.copySuccess"));
    } catch (error) {
      toast.error(t("profile.copyFailure"));
    }
  };

  async function onSignOut() {
    Auth.signOut();
    ExpenseTrackingMetadataDB.deleteAllItems();
    await delay(1);
    setAppRefresh(Math.random());
  }

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  async function updateProfile(updateValues) {
    return userProfileService.updateUserProfile({
      newProfile: { ...updateValues, userId },
      callback: ({ userProfile, error }) => {
        if (error) return;
        setAppRefresh(Math.random());
      },
    });
  }

  const handleSaveConfig = async (updatedConfig) => {
    try {
      setLoading(true);
      const editingConfig = fieldConfigs.find((item) => item.fieldName === updatedConfig.fieldName);

      if (!editingConfig) {
        const { label = "" } = updatedConfig;
        const fieldName = label
          .trim()
          .replace(/[^a-zA-Z ]/g, "")
          .replace(/ /g, "_");
        if (!fieldName) {
          toast.error(t("profile.invalidFieldName"));
          return;
        }
        updatedConfig = { ...updatedConfig, fieldName };
      }

      const updatedProfile = {
        fieldConfigs: editingConfig
          ? fieldConfigs.map((c) => (c.fieldName === updatedConfig.fieldName ? updatedConfig : c))
          : [...fieldConfigs, updatedConfig],
      };

      await updateProfile(updatedProfile);
      toast.success(editingConfig ? t("profile.fieldUpdated") : t("profile.fieldAdded"));
    } catch (error) {
      toast.error(error.message);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="my-5 profile-container">
      <Row className="justify-content-center">
        <Col lg={10} xl={8}>
          {/* Header Card */}
          <Card className="shadow-sm mb-4 border-0 profile-header-card">
            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <div className="koala-avatar mb-3">
                  <FaPaw size={80} className="bg-success rounded-circle p-3 text-white avatar-icon" />
                </div>
                <h2 className="mb-1 profile-display-name">
                  <FaUser className="me-2 text-muted" />
                  {userProfile.displayName}
                </h2>
                <small className="text-muted profile-member-status">{t("profile.memberStatus")}</small>

                <div className="mt-4 d-flex gap-2 justify-content-center">
                  <EditableModal
                    title={t("profile.updateProfile")}
                    configs={[
                      {
                        fieldName: "displayName",
                        label: t("profile.displayName"),
                        inputType: INPUT_TYPE.TEXT,
                        required: true,
                      },
                    ]}
                    actionLabel={t("profile.update")}
                    currentValue={userProfile}
                    actionOnClick={updateProfile}
                    buttonVariant="outline-success"
                  />

                  <Button variant="outline-danger" onClick={onSignOut} className="d-inline-flex align-items-center">
                    <FaSignOutAlt className="me-2" />
                    {t("profile.signOut")}
                  </Button>
                </div>
              </div>

              {/* Account Info */}
              <Row className="g-4">
                <Col md={6}>
                  <Card className="h-100 border-0 info-card">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-3">
                        <FaEnvelope className="me-3 text-success flex-shrink-0" size={20} />
                        <div>
                          <h6 className="mb-0 fw-bold">{t("profile.loginEmail")}</h6>
                          <p className="text-muted mb-0 small">{userProfile.emailAddress}</p>
                        </div>
                      </div>

                      <div className="d-flex align-items-center">
                        <FaEnvelope className="me-3 text-success flex-shrink-0" size={20} />
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <h6 className="mb-0 fw-bold">{t("profile.receiptEmail")}</h6>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={handleCopyEmail}
                              className="p-0 text-success"
                              title={t("profile.copy")}
                            >
                              <FaCopy />
                            </Button>
                          </div>
                          <code className="text-muted small d-block">{receiptEmail}</code>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="h-100 border-0 info-card">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-3">
                        <FaCalendarAlt className="me-3 text-success flex-shrink-0" size={20} />
                        <div>
                          <h6 className="mb-0 fw-bold">{t("profile.memberSince")}</h6>
                          <p className="text-muted mb-0 small">{formatDate(userProfile.createdDate)}</p>
                        </div>
                      </div>

                      <div className="stats-card bg-success text-white">
                        <FaReceipt size={24} className="mb-2" />
                        <h3 className="mb-0">{trackingItems.length}</h3>
                        <small>{t("profile.receiptsUploaded")}</small>
                      </div>

                      <Button variant="outline-danger" size="sm" className="mt-3 w-100" onClick={handleDeleteAllExpenses}>
                        <FaTrash className="me-2" />
                        {t("DeleteAllExpense.title")}
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Field Configurations */}
          <Card className="shadow-sm border-0 mt-4">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h4 className="mb-0 d-flex align-items-center">
                    <FaCog className="me-2 text-success" />
                    {t("profile.fieldConfigurations")}
                  </h4>
                  <p className="text-muted small mb-0 mt-1">{t("profile.fieldConfigurationsHint")}</p>
                </div>

                {fieldConfigs.length < 5 && (
                  <EditableModal
                    title={t("profile.addNewField")}
                    configs={CREATE_CONFIG_CONFIGS}
                    actionLabel={t("profile.create")}
                    currentValue={{}}
                    actionOnClick={handleSaveConfig}
                    buttonVariant="success"
                    className="floating-add-button"
                  />
                )}
              </div>

              {fieldConfigs.length === 0 ? (
                <div className="text-center py-4 text-muted">{t("profile.noFieldsConfigured")}</div>
              ) : (
                <div className="config-grid">
                  {fieldConfigs.map((config, index) => (
                    <Card key={index} className="config-item">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1 fw-bold">{config.label}</h6>
                            <div className="d-flex gap-2 small">
                              <Badge bg="success" className="text-uppercase">
                                {config.inputType}
                              </Badge>
                              {config.showInDashboard && <Badge bg="info">{t("profile.dashboard")}</Badge>}
                            </div>
                            <p className="text-muted small mb-0 mt-2">{config.description || t("profile.noDescription")}</p>
                          </div>
                          <EditableModal
                            title={t("profile.update")}
                            configs={CREATE_CONFIG_CONFIGS.filter((i) => i.fieldName !== "label")}
                            actionLabel={t("profile.update")}
                            currentValue={config}
                            actionOnClick={(data) => handleSaveConfig({ ...config, ...data })}
                            buttonVariant="link"
                            size="sm"
                          />
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete All Modal */}
      <ConfirmModal
        show={showDeleteAllModal}
        title={t("DeleteAllExpense.title")}
        subtitle={t("DeleteAllExpense.subtitle")}
        onCancel={() => setShowDeleteAllModal(false)}
        onConfirm={async () => {
          setLoading(true);
          for (const item of trackingItems) {
            const { expenseId } = item;
            await userProfileService.deleteExpenseEvent({
              expenseId: expenseId,
              callback: ({ err, data }) => {
                if (err) {
                  toast.error(err);
                  return;
                }
                ExpenseTrackingMetadataDB.deleteItemFromExpenseTrackingMetadata(expenseId);
                toast.warn("Expense deleted successfully: " + item.title);
              },
            });
            await delay(1);
          }
          setLoading(false);
          setShowDeleteAllModal(false);
        }}
      />

      <style jsx>{`
        .profile-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .profile-header-card {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9f5ec 100%);
          border-radius: 1rem;
        }

        .info-card {
          background: rgba(255, 255, 255, 0.9);
          border-radius: 0.75rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .stats-card {
          padding: 1.5rem;
          border-radius: 0.75rem;
          background: #198754;
          text-align: center;
          transition: transform 0.2s;

          &:hover {
            transform: translateY(-2px);
          }
        }

        .config-grid {
          display: grid;
          gap: 1rem;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        }

        .config-item {
          transition: all 0.2s;
          border-radius: 0.75rem;

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          }
        }

        .floating-add-button {
          position: relative;
          &:before {
            content: "+";
            position: absolute;
            right: -10px;
            top: -10px;
            background: #198754;
            color: white;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
          }
        }

        .avatar-icon {
          box-shadow: 0 4px 12px rgba(25, 135, 84, 0.2);
        }

        @media (max-width: 768px) {
          .profile-display-name {
            font-size: 1.5rem;
          }

          .profile-member-status {
            font-size: 0.8rem;
          }
        }
      `}</style>
    </Container>
  );
};
export default ProfilePage;
